import React, { useEffect, useMemo, useState } from "react";
import styles from "./RightSidePanel.module.css";
import { Button, Chip, Paper, Typography } from "@mui/material";
import {
  PersonAddAlt1Outlined,
  PersonOutline,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetAllCartItemsQuery } from "../../Store/Services/CartService";
import {
  openCustomerDialog,
  clearSelectedCustomer,
} from "../../Store/Slices/customerSlice";
import CustomerDialog from "../Customer/CustomerDialog";
import CartDetails from "../Cart/cartDetails";
import OrderSummery from "../Cart/orderSummery";
import { getGlobalCartId } from "../../Utils/cartSession";
import { matchOffersToItems } from "../../Utils/orderTotals";

const getActiveCustomer = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = sessionStorage.getItem("posCustomer");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const getActiveCartId = () => getGlobalCartId();

const getCurrency = () => {
  if (typeof window === "undefined") {
    return {
      currencySymbol: "AED",
      currencyRate: 1,
    };
  }

  return {
    currencySymbol: sessionStorage.getItem("currencySymbol") || "AED",

    currencyRate: Number(sessionStorage.getItem("currencyRate")) || 1,
  };
};

const RightSidePanel = ({
  mobileCompact = false,
  mobileExpanded = true,
  mobileSheet = false,
  onMobileToggle,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [scanFlash, setScanFlash] = useState(false);

  const [customer, setCustomer] = useState(() => getActiveCustomer());

  const [cartId, setCartId] = useState(() =>
    typeof window !== "undefined" ? getActiveCartId() : null,
  );

  const [currency, setCurrency] = useState(() => getCurrency());

  const handleViewSummary = () => {
    if (mobileCompact && onMobileToggle) {
      onMobileToggle();
      return;
    }

    navigate("/billing");
  };

  const handleAddCustomer = () => {
    dispatch(openCustomerDialog());
  };

  const handleRemoveCustomer = () => {
    dispatch(clearSelectedCustomer());
    try {
      sessionStorage.removeItem("posCustomer");
    } catch {
      // ignore storage failures
    }
    setCustomer(null);
    window.dispatchEvent(
      new CustomEvent("pos:customer-changed", { detail: { customer: null } }),
    );
  };

  const customerName =
    customer &&
    (customer.custName ||
      `${customer.custFirstName || ""} ${customer.custLastName || ""}`.trim() ||
      customer.custMobile ||
      customer.custEmail ||
      "Customer");

  let items = [];
  let orderDet;

  const { data, isSuccess } = useGetAllCartItemsQuery(cartId, {
    skip: !cartId,
  });

  if (isSuccess) {
    items = data?.orderDetails?.items || [];
    orderDet = data?.orderDetails;
  }

  const currentItemsCount = useMemo(
    () =>
      (items || []).reduce((sum, item) => sum + (Number(item?.qty) || 0), 0),
    [items],
  );

  // The API's totPay is the amount to collect; only compute it locally if it is absent.
  const currentTotalPay = useMemo(() => {
    if (orderDet?.totPay !== undefined && orderDet?.totPay !== null) {
      return Math.max(0, Number(orderDet.totPay) || 0);
    }

    const subtotal = (items || []).reduce(
      (sum, item) => sum + (Number(item?.amt) || 0) * (Number(item?.qty) || 0),
      0,
    );

    const tax = Number(orderDet?.totTax) || 0;
    const charges = Number(orderDet?.totChg) || 0;
    const offer = Number(orderDet?.totOff) || 0;
    const coupon = Number(orderDet?.couponAmount) || 0;

    return Math.max(0, subtotal + tax + charges - offer - coupon);
  }, [items, orderDet]);

  const itemOffers = useMemo(
    () => matchOffersToItems(items, orderDet),
    [items, orderDet],
  );

  useEffect(() => {
    const handleCartIdChange = (event) => {
      const nextCartId = event?.detail?.cartId ?? getGlobalCartId();

      if (!nextCartId) {
        setCartId(null);
        return;
      }

      setCartId(String(nextCartId));
    };

    const handleCurrencyChange = () => {
      setCurrency({
        currencySymbol: sessionStorage.getItem("currencySymbol") || "AED",

        currencyRate: Number(sessionStorage.getItem("currencyRate")) || 1,
      });
    };

    const handleCustomerChange = (event) => {
      setCustomer(event?.detail?.customer ?? getActiveCustomer());
    };

    let timer = null;

    const handleScanSuccess = () => {
      setScanFlash(true);

      if (timer) {
        clearTimeout(timer);
      }

      timer = setTimeout(() => setScanFlash(false), 700);
    };

    window.addEventListener("pos:cart-id-changed", handleCartIdChange);

    window.addEventListener("pos:scan-add-success", handleScanSuccess);

    window.addEventListener("currency-changed", handleCurrencyChange);

    window.addEventListener("pos:customer-changed", handleCustomerChange);

    return () => {
      window.removeEventListener("pos:cart-id-changed", handleCartIdChange);

      window.removeEventListener("pos:scan-add-success", handleScanSuccess);

      window.removeEventListener("currency-changed", handleCurrencyChange);

      window.removeEventListener("pos:customer-changed", handleCustomerChange);

      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  if (mobileCompact && !mobileExpanded) {
    return (
      <Paper
        className={`${styles.compactContainer} ${
          scanFlash ? styles.scanFlash : ""
        } ${styles.mobileSaleBar}`}
      >
        <div
          className={`${styles["sale-header"]} ${styles.mobileSaleHeader}`}
          onClick={onMobileToggle}
          role="button"
          tabIndex={0}
          aria-expanded={false}
        >
          <div className={styles.saleHeaderLeft}>
            <div className={styles["bills-heading"]}>Current Sale</div>

            <div className={styles.mobileSummaryStats}>
              <Typography className={styles.mobileSummaryCount}>
                {currentItemsCount} item
                {currentItemsCount === 1 ? "" : "s"}
              </Typography>

              <Typography className={styles.mobileSummaryTotal}>
                Total {currency.currencySymbol}{" "}
                {(currentTotalPay * currency.currencyRate).toFixed(2)}
              </Typography>
            </div>
          </div>

          <div className={styles.saleHeaderRight}>
            {cartId && (
              <button
                className={styles["invoice-link"]}
                onClick={(event) => {
                  event.stopPropagation();
                  onMobileToggle?.();
                }}
              >
                {`#INV-${String(cartId).padStart(6, "0")}`}
              </button>
            )}

            <span className={styles.saleChevron}>▼</span>
          </div>
        </div>
      </Paper>
    );
  }

  return (
    <Paper
      className={`${styles.container} ${scanFlash ? styles.scanFlash : ""} ${
        mobileSheet ? styles.mobileSheet : ""
      } ${
        mobileCompact
          ? mobileExpanded
            ? styles.mobileSalePopup
            : styles.mobileSaleBar
          : ""
      }`}
    >
      {!mobileSheet && (
        <div
          className={`${styles["sale-header"]} ${
            mobileCompact ? styles.mobileSaleHeader : ""
          }`}
          onClick={mobileCompact ? onMobileToggle : undefined}
          role={mobileCompact ? "button" : undefined}
          tabIndex={mobileCompact ? 0 : undefined}
          aria-expanded={mobileCompact ? mobileExpanded : true}
        >
          <div className={styles.saleHeaderLeft}>
            <div className={styles["bills-heading"]}>Current Sale</div>

            <Typography className={styles.saleMetaText}>
              {currentItemsCount} item
              {currentItemsCount === 1 ? "" : "s"}
              {" · Total "}
              {currency.currencySymbol}{" "}
              {(currentTotalPay * currency.currencyRate).toFixed(2)}
            </Typography>
          </div>

          <div className={styles.saleHeaderRight}>
            {cartId && (
              <button
                className={styles["invoice-link"]}
                onClick={(event) => {
                  event.stopPropagation();
                  handleViewSummary();
                }}
              >
                {`#INV-${String(cartId).padStart(6, "0")}`}
              </button>
            )}

            {mobileCompact && (
              <span className={styles.saleChevron}>
                {mobileExpanded ? "▲" : "▼"}
              </span>
            )}
          </div>
        </div>
      )}

      {(!mobileCompact || mobileExpanded) && (
        <div className={styles["cream-panel"]}>
          <div className={styles.customerRow}>
            {customer ? (
              <Chip
                icon={<PersonOutline />}
                label={customerName}
                onDelete={handleRemoveCustomer}
                deleteIcon={<CloseIcon />}
                onClick={handleAddCustomer}
                color="primary"
                variant="outlined"
                sx={{ maxWidth: "100%", fontWeight: 600 }}
              />
            ) : (
              <Button
                fullWidth
                variant="contained"
                size="small"
                startIcon={<PersonAddAlt1Outlined />}
                onClick={handleAddCustomer}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#0f766e",
                  "&:hover": { backgroundColor: "#0b5952" },
                }}
              >
                Customer
              </Button>
            )}
          </div>

          <CartDetails items={items} itemOffers={itemOffers} />

          <OrderSummery
            items={items}
            orderDet={orderDet}
            cartId={cartId}
            currency={currency}
          />
        </div>
      )}

      <CustomerDialog />
    </Paper>
  );
};

export default RightSidePanel;
